import {
  useCallback,
  useRef,
  useState,
  useEffect,
  useMemo,
  type MouseEvent,
} from "react";
import { motion } from "framer-motion";
import { Clock } from "@phosphor-icons/react";
import { cn } from "../../lib/cn";

export interface VersionTimelineVersion {
  id: string;
  snapshot_name: string | null;
  created_at: string;
}

interface VersionTimelineProps {
  versions: VersionTimelineVersion[];
  selectedVersionId: string | null;
  onSelect: (versionId: string) => void;
  className?: string;
}

const MIN_MARKER_GAP = 36;
const TRACK_PAD = 14;
const SCRUBBER_SIZE = 20;
const MARKER_HIT = 16;

function formatTimestamp(iso: string): string {
  const date = new Date(iso);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return "Just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: date.getFullYear() !== now.getFullYear() ? "numeric" : undefined,
  });
}

function formatFullDateTime(iso: string): string {
  const date = new Date(iso);
  return date.toLocaleString(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  });
}

export function formatVersionTooltip(
  version: VersionTimelineVersion,
): string {
  const name = version.snapshot_name?.trim();
  const abs = formatFullDateTime(version.created_at);
  const rel = formatTimestamp(version.created_at);
  if (name) return `${name}\n${abs} · ${rel}`;
  return `Snapshot · ${abs} · ${rel}`;
}

export function formatVersionShortLabel(
  version: VersionTimelineVersion,
): string {
  return version.snapshot_name?.trim() || formatTimestamp(version.created_at);
}

export function VersionTimeline({
  versions,
  selectedVersionId,
  onSelect,
  className,
}: VersionTimelineProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  const [containerW, setContainerW] = useState(0);
  const [isDragging, setIsDragging] = useState(false);

  const sorted = useMemo(
    () =>
      [...versions].sort(
        (a, b) =>
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
      ),
    [versions],
  );

  const n = sorted.length;

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const ro = new ResizeObserver(([e]) => setContainerW(e.contentRect.width));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const usable = Math.max(0, containerW - TRACK_PAD * 2);
  const needed = n > 1 ? (n - 1) * MIN_MARKER_GAP : 0;
  const scrollable = needed > usable && usable > 0;
  const trackW = scrollable ? needed : usable;

  const getMarkerX = useCallback(
    (i: number) => (n <= 1 ? trackW / 2 : (i / (n - 1)) * trackW),
    [n, trackW],
  );

  const selIdx = selectedVersionId
    ? sorted.findIndex((v) => v.id === selectedVersionId)
    : -1;

  // Auto-scroll selected marker into the visible center
  useEffect(() => {
    if (selIdx < 0 || !scrollable || !scrollRef.current) return;
    const x = getMarkerX(selIdx) + TRACK_PAD;
    scrollRef.current.scrollTo({
      left: Math.max(0, x - containerW / 2),
      behavior: "smooth",
    });
  }, [selIdx, scrollable, containerW, getMarkerX]);

  const idxFromPointer = useCallback(
    (clientX: number) => {
      const el = trackRef.current;
      if (!el || n === 0) return null;
      const r = el.getBoundingClientRect();
      const pct = Math.max(0, Math.min(1, (clientX - r.left) / (trackW || 1)));
      return Math.round(pct * Math.max(0, n - 1));
    },
    [n, trackW],
  );

  const handleTrackClick = useCallback(
    (e: MouseEvent<HTMLDivElement>) => {
      const i = idxFromPointer(e.clientX);
      if (i != null && sorted[i]) onSelect(sorted[i].id);
    },
    [idxFromPointer, sorted, onSelect],
  );

  const handleDown = useCallback((e: React.PointerEvent) => {
    e.preventDefault();
    setIsDragging(true);
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
  }, []);

  const handleMove = useCallback(
    (e: React.PointerEvent) => {
      if (!isDragging) return;
      const i = idxFromPointer(e.clientX);
      if (i != null && sorted[i] && sorted[i].id !== selectedVersionId)
        onSelect(sorted[i].id);
    },
    [isDragging, idxFromPointer, sorted, selectedVersionId, onSelect],
  );

  const handleUp = useCallback((e: React.PointerEvent) => {
    (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId);
    setIsDragging(false);
  }, []);

  const handleKey = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key !== "ArrowLeft" && e.key !== "ArrowRight") return;
      e.preventDefault();
      const next = Math.max(
        0,
        Math.min(n - 1, selIdx + (e.key === "ArrowLeft" ? -1 : 1)),
      );
      if (sorted[next]) onSelect(sorted[next].id);
    },
    [selIdx, n, sorted, onSelect],
  );

  if (n === 0) {
    return (
      <div className={cn("flex items-center justify-center py-5", className)}>
        <p className="text-[11px] text-[var(--text-muted)]">
          No snapshots yet — create one to track history
        </p>
      </div>
    );
  }

  const stagger = Math.min(0.02, 0.4 / Math.max(1, n));
  const sx = selIdx >= 0 ? getMarkerX(selIdx) : getMarkerX(0);

  return (
    <div className={cn("flex flex-col gap-2.5", className)}>
      <div
        className={cn(
          "relative rounded-[var(--radius-sm)]",
          "border border-[var(--border-hairline)] bg-[var(--bg-secondary)]",
          "transition-colors duration-75 hover:border-[var(--border-subtle)]",
        )}
      >
        {/* Scrollable viewport */}
        <div
          ref={scrollRef}
          className="relative h-10 overflow-x-auto overflow-y-hidden scrollbar-none"
          tabIndex={0}
          role="slider"
          aria-valuemin={0}
          aria-valuemax={n - 1}
          aria-valuenow={selIdx}
          aria-label="Version timeline"
          onKeyDown={handleKey}
        >
          {/* Track surface */}
          <div
            ref={trackRef}
            className="relative h-full cursor-pointer select-none"
            style={{
              width: trackW,
              marginLeft: TRACK_PAD,
              marginRight: TRACK_PAD,
            }}
            onClick={handleTrackClick}
          >
            {/* Base line */}
            <div
              className="absolute inset-x-0 top-1/2 h-px -translate-y-1/2 bg-[var(--border-subtle)]"
              aria-hidden
            />

            {/* Filled progress line to selected marker */}
            {selIdx >= 0 && (
              <motion.div
                className="absolute top-1/2 left-0 h-px -translate-y-1/2 origin-left"
                style={{
                  background: "var(--color-lever-orange)",
                  opacity: 0.35,
                }}
                initial={false}
                animate={{ width: getMarkerX(selIdx) }}
                transition={{ type: "spring", stiffness: 200, damping: 30 }}
                aria-hidden
              />
            )}

            {/* Markers */}
            {sorted.map((v, i) => {
              const sel = v.id === selectedVersionId;
              return (
                <motion.button
                  key={v.id}
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onSelect(v.id);
                  }}
                  initial={{ opacity: 0, scale: 0 }}
                  animate={{ opacity: 1, scale: sel ? 1.35 : 1 }}
                  whileHover={{ scale: 1.4 }}
                  whileTap={{ scale: 0.85 }}
                  transition={{
                    type: "spring",
                    stiffness: 500,
                    damping: 28,
                    opacity: { duration: 0.2, delay: i * stagger },
                  }}
                  className="absolute top-1/2 z-10 flex items-center justify-center focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[var(--color-lever-orange)]/40"
                  style={{
                    left: getMarkerX(i),
                    width: MARKER_HIT,
                    height: MARKER_HIT,
                    marginLeft: -MARKER_HIT / 2,
                    marginTop: -MARKER_HIT / 2,
                  }}
                  title={formatVersionTooltip(v)}
                  aria-label={`Version: ${formatVersionShortLabel(v)}, ${formatFullDateTime(v.created_at)}`}
                >
                  <div
                    className={cn(
                      "rounded-full transition-colors duration-150",
                      sel
                        ? "h-2.5 w-2.5 bg-[var(--color-lever-orange)]"
                        : "h-[7px] w-[7px] bg-[var(--text-muted)] hover:bg-[var(--color-lever-orange)]",
                    )}
                    style={
                      sel
                        ? { boxShadow: "0 0 8px rgba(255,92,0,0.5)" }
                        : undefined
                    }
                  />
                </motion.button>
              );
            })}

            {/* Scrubber handle */}
            <motion.div
              onPointerDown={handleDown}
              onPointerMove={handleMove}
              onPointerUp={handleUp}
              onPointerLeave={handleUp}
              initial={false}
              animate={{ x: sx, scale: isDragging ? 1.1 : 1 }}
              transition={{ type: "spring", stiffness: 340, damping: 28 }}
              className={cn(
                "absolute top-1/2 z-20 touch-none select-none",
                "flex items-center justify-center rounded-full",
                "bg-[var(--bg-surface)] border border-[var(--border-default)]",
                "shadow-[var(--shadow-sm)]",
                "hover:border-[var(--color-lever-orange)]/50",
                isDragging ? "cursor-grabbing" : "cursor-grab",
              )}
              style={{
                width: SCRUBBER_SIZE,
                height: SCRUBBER_SIZE,
                marginLeft: -SCRUBBER_SIZE / 2,
                marginTop: -SCRUBBER_SIZE / 2,
                left: 0,
              }}
              title={
                selIdx >= 0 && sorted[selIdx]
                  ? formatVersionTooltip(sorted[selIdx])
                  : "Drag to scrub versions"
              }
            >
              <Clock
                size={11}
                weight="fill"
                className="text-[var(--color-lever-orange)]"
              />
            </motion.div>
          </div>
        </div>

        {/* Edge fade indicators when scrollable */}
        {scrollable && (
          <>
            <div
              className="pointer-events-none absolute inset-y-0 left-0 w-5 z-30 rounded-l-[var(--radius-sm)]"
              style={{
                background:
                  "linear-gradient(to right, var(--bg-secondary), transparent)",
              }}
              aria-hidden
            />
            <div
              className="pointer-events-none absolute inset-y-0 right-0 w-5 z-30 rounded-r-[var(--radius-sm)]"
              style={{
                background:
                  "linear-gradient(to left, var(--bg-secondary), transparent)",
              }}
              aria-hidden
            />
          </>
        )}
      </div>

      {/* End labels */}
      <div className="flex justify-between gap-2 min-h-[14px]">
        {sorted[0] && (
          <span
            className="text-[10px] text-[var(--text-muted)] truncate max-w-[45%]"
            title={formatVersionTooltip(sorted[0])}
          >
            {formatVersionShortLabel(sorted[0])}
          </span>
        )}
        {n > 1 && sorted[n - 1] && (
          <span
            className="text-[10px] text-[var(--text-muted)] truncate max-w-[45%] text-right"
            title={formatVersionTooltip(sorted[n - 1])}
          >
            {formatVersionShortLabel(sorted[n - 1])}
          </span>
        )}
      </div>
    </div>
  );
}
